import { type FC } from "react";
import { cn } from "@/ui/lib/utils";
import { Checkbox } from "@/ui/components/form/Checkbox";
import { Column } from "@/ui/components/layout/Column";
import { Container } from "@/ui/components/layout/Container";
import { Grid } from "@/ui/components/layout/Grid";
import { Button } from "@/ui/components/primitives/Button";
import { TextSubtitle } from "@/ui/components/typography/TextSubtitle";
import { TextTitle } from "@/ui/components/typography/TextTitle";
import { type TLoginWrapperProps } from "./LoginWrapper.config";
import { useLoginWrapper } from "./useLoginWrapper";

export const LoginWrapper: FC<TLoginWrapperProps> = (
  {
    title,
    subtitle,
    children,
    defaultLoginType,
    loginType,
    onLoginTypeChange,
    toggle,
    rememberMe,
    className,
    ...props
  },
) => {
  const {
    activeLoginType,
    handleToggleClick,
    resolvedRememberMe,
    shouldRenderRememberMe,
    subtitle: resolvedSubtitle,
    title: resolvedTitle,
    toggleLabel,
  } = useLoginWrapper({
    title,
    subtitle,
    defaultLoginType,
    loginType,
    onLoginTypeChange,
    toggle,
    rememberMe,
  });

  return (
    <Container
      {...props}
      tag="section"
      data-component="LoginWrapper"
      className={cn(
        "w-full",
        className,
      )}
    >
      <Grid
        gap="none"
        className="justify-items-center gap-6"
      >
        <Column className="justify-center">
          <header
            data-component="LoginWrapperHeader"
            className="grid w-full justify-items-center gap-2 text-center"
          >
            <TextTitle
              tag="h1"
              size={3}
              align="center"
              isResponsive={false}
            >
              { resolvedTitle }
            </TextTitle>

            <TextSubtitle
              tag="span"
              size={3}
              align="center"
              isResponsive={false}
              className="block [&]:text-foreground-muted-strong"
            >
              { resolvedSubtitle }
            </TextSubtitle>

          </header>
        </Column>

        <Column className="w-full justify-center">
          <Grid
            gap="4"
            data-component="LoginWrapperBody"
            className="justify-items-center"
          >
            <Column className="w-full">
              <section
                data-component="LoginWrapperContent"
                className="w-full"
              >
                { children({ loginType: activeLoginType }) }
              </section>
            </Column>

            <Column className="justify-center">
              <Button
                variant="underline"
                onClick={handleToggleClick}
              >
                { toggleLabel }
              </Button>
            </Column>

            { shouldRenderRememberMe ? (
              <Column className="w-full justify-start">
                <Checkbox
                  label={resolvedRememberMe.label}
                  input={resolvedRememberMe.input}
                  className="w-full px-1 py-2"
                />
              </Column>
            ) : null }
          </Grid>
        </Column>
      </Grid>
    </Container>
  );
};

LoginWrapper.displayName = "LoginWrapper";
